'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import getComponentName from 'react-utils/getComponentName';
import * as FilesActions from 'DesignData/actions/finder/Files';
import * as OptionsActions from 'DesignData/actions/finder/Options';
import { FilePropTypes, FolderPropTypes } from 'DesignData/propTypes/finder';
import * as finderSelectors from 'DesignData/selectors/finder';
import { getActiveLayoutId } from 'DesignData/selectors/app';
import { makeGetFinderWidgetCatalog } from 'DesignData/selectors/layoutWidgets';
import { canDragIntoLayout } from 'DesignData/lib/layouts/widgets/dnd';
/**
 * Connect
 */

function makeMapStateToProps() {
  const getFileProps = finderSelectors.makeGetFileProps();
  const getFinderWidgetCatalog = makeGetFinderWidgetCatalog();
  return function mapStateToProps(state, props) {
    const activeLayoutId = getActiveLayoutId(state, props);
    const fileProps = getFileProps(state, Object.assign({}, props, {
      layoutId: activeLayoutId
    }));
    const finderWidgetCatalog = getFinderWidgetCatalog(state, Object.assign({}, props, {
      layoutId: activeLayoutId
    }));
    const catalogWidget = finderWidgetCatalog[props.fileId];
    return Object.assign({}, fileProps, {
      catalogWidget,
      canDragIntoLayout: canDragIntoLayout(catalogWidget)
    });
  };
}

const mapDispatchToProps = {
  deleteFile: FilesActions.deleteFile,
  duplicateFile: FilesActions.duplicateFile,
  duplicateLayoutToTemplate: FilesActions.duplicateLayoutToTemplate,
  hideRenameConfirm: OptionsActions.hideRenameFileConfirm,
  renameFile: FilesActions.renameFile,
  showRevisions: FilesActions.showRevisions,
  toggleRenameAsset: OptionsActions.toggleRenameAsset,
  showDependents: FilesActions.showDependents,
  copyPublicUrl: FilesActions.copyPublicUrl,
  copyModuleSnippet: FilesActions.copyModuleSnippet,
  viewLayoutSource: FilesActions.viewLayoutSource
};
/**
 * High Order Component connecting files to Redux store
 */

export default function withConnectedFile(WrappedComponent) {
  class HighOrderConnectedFile extends PureComponent {
    constructor(...args) {
      super(...args);

      this.deleteFile = () => {
        const {
          fileId,
          folderId
        } = this.props;
        this.props.deleteFile(fileId, folderId);
      };

      this.duplicateFile = () => {
        this.props.duplicateFile(this.props.fileId);
      };

      this.renameFile = fileName => {
        this.props.renameFile(this.props.fileId, fileName);
      };

      this.showRevisions = () => {
        this.props.showRevisions(this.props.fileId);
      };

      this.toggleRenameFile = () => {
        this.toggleRenameTimeout = setTimeout(() => {
          this.props.toggleRenameAsset(this.props.fileId);
        }, 0);
      };

      this.showDependents = () => {
        this.props.showDependents(this.props.fileId);
      };

      this.duplicateLayoutToTemplate = () => {
        this.props.duplicateLayoutToTemplate(this.props.fileId);
      };

      this.copyPublicUrl = () => {
        this.props.copyPublicUrl(this.props.file);
      };

      this.copyModuleSnippet = () => {
        this.props.copyModuleSnippet(this.props.fileId);
      };

      this.viewLayoutSource = () => {
        this.props.viewLayoutSource(this.props.fileId);
      };
    }

    componentWillUnmount() {
      clearTimeout(this.toggleRenameTimeout);
    }

    render() {
      /* Out of sync issues will sometimes look for a file after it's deleted */
      if (!this.props.file) return null;
      return /*#__PURE__*/_jsx(WrappedComponent, Object.assign({}, this.props, {
        deleteFile: this.deleteFile,
        duplicateFile: this.duplicateFile,
        renameFile: this.renameFile,
        showRevisions: this.showRevisions,
        toggleRenameFile: this.toggleRenameFile,
        showDependents: this.showDependents,
        duplicateLayoutToTemplate: this.duplicateLayoutToTemplate,
        copyPublicUrl: this.copyPublicUrl,
        copyModuleSnippet: this.copyModuleSnippet,
        viewLayoutSource: this.viewLayoutSource
      }));
    }

  }

  HighOrderConnectedFile.propTypes = {
    deleteFile: PropTypes.func.isRequired,
    duplicateFile: PropTypes.func.isRequired,
    duplicateLayoutToTemplate: PropTypes.func.isRequired,
    file: FilePropTypes.file,
    fileId: FilePropTypes.fileId.isRequired,
    fileName: FilePropTypes.fileName,
    filePermissions: FilePropTypes.filePermissions,
    fileTemplateType: FilePropTypes.fileTemplateType,
    fileType: FilePropTypes.fileType,
    hideRenameConfirm: PropTypes.func.isRequired,
    isRenamingFile: FilePropTypes.isRenamingFile,
    folderId: FolderPropTypes.folderId,
    renameFile: PropTypes.func.isRequired,
    showRevisions: PropTypes.func.isRequired,
    toggleRenameAsset: PropTypes.func.isRequired,
    showDependents: PropTypes.func.isRequired,
    copyPublicUrl: PropTypes.func.isRequired,
    copyModuleSnippet: PropTypes.func.isRequired,
    canDragIntoLayout: PropTypes.bool.isRequired,
    catalogWidget: PropTypes.object,
    viewLayoutSource: PropTypes.func.isRequired
  };
  HighOrderConnectedFile.displayName = `withConnectedFile(${getComponentName(WrappedComponent)})`;
  return connect(makeMapStateToProps, mapDispatchToProps)(HighOrderConnectedFile);
}